import { useState, useEffect } from 'react';
import {
  fetchPortfolioBalances,
  PortfolioBalancesResponse,
} from 'views/Portfolio/components/Summary/services/fetchPortfolioBalances';
import { PortfolioBalance } from 'types/DomainTypes';
import { LLMConfig } from '../../AIChat/types';
import { PortfolioTier, getPortfolioTier, getTierConfigOverride } from '../services/tierConfig';

interface UsePortfolioTierReturn {
  tier: PortfolioTier;
  portfolioValue: number;
  configOverride: Partial<LLMConfig>;
  isLoading: boolean;
}

export const usePortfolioTier = (): UsePortfolioTierReturn => {
  const [tier, setTier] = useState<PortfolioTier>('standard');
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadTier = async () => {
      try {
        const response: PortfolioBalancesResponse = await fetchPortfolioBalances();
        const rawData: PortfolioBalance[] = response?.data || [];

        if (cancelled) return;

        // Filter out the "All Portfolios" summary row (null/empty portfolioId) to avoid double-counting
        const individualPortfolios = rawData.filter(
          (p) => p.portfolioId != null && String(p.portfolioId).trim() !== '',
        );
        const portfolios = individualPortfolios.length > 0 ? individualPortfolios : rawData;
        const effectiveValue = portfolios.reduce((sum, p) => sum + (p.currentHoldings || 0), 0);

        const detectedTier = getPortfolioTier(effectiveValue);
        setTier(detectedTier);
        setPortfolioValue(effectiveValue);
      } catch {
        // Failed to detect portfolio tier — fall back to default 'standard'
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadTier();
    return () => {
      cancelled = true;
    };
  }, []);

  const configOverride = getTierConfigOverride(tier);

  return { tier, portfolioValue, configOverride, isLoading };
};
